import { message } from "antd";
import axios from "axios";
import { errorActions } from "../helpers/auth_helper";

let userDetailsCache: { user: any; ts: number } | null = null;
let userDetailsInFlight: Promise<any> | null = null;
const USER_DETAILS_TTL = 60 * 1000;

export const fetchUserDetails = async () => {
  if (userDetailsCache && Date.now() - userDetailsCache.ts < USER_DETAILS_TTL) {
    return userDetailsCache.user;
  }
  if (userDetailsInFlight) {
    return userDetailsInFlight;
  }
  userDetailsInFlight = axios
    .get(`${process.env.REACT_APP_API_URL}/procurement-current-user/`)
    .then((res) => {
      const user = res.data;
      userDetailsCache = { user, ts: Date.now() };
      return user;
    })
    .catch((error) => {
      errorActions(error, "Error fetching details. Please try again.");
      if (error.response?.status !== 200) {
        return window.location.replace("/login");
      }
      return;
    })
    .finally(() => {
      userDetailsInFlight = null;
    });
  return userDetailsInFlight;
};

export const fetchBookings = async (params: any) => {
  try {
    return axios
      .get(`${process.env.REACT_APP_API_URL}/book-a-slot/`, {
        ...params,
      })
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status == 403) {
          return window.location.replace("/login");
        }
        return;
      });
  } catch (error) {}
};

export async function editUser(values: any, id: string) {
  try {
    await axios
      .put(`${process.env.REACT_APP_API_URL}/user/` + id + "/", {
        ...values,
      })
      .then((response) => {
        if (response.data) {
          message.success("Client edited successfully");
          return response.data;
        }
      })
      .catch((error) => {
        errorActions(error, "Error editing client. Please try again");
        return;
      });
  } catch (error) {}
}

export const getUserCumulativeSum = async (params: any) => {
  try {
    return axios
      .get(`${process.env.REACT_APP_API_URL}/user-order-sum/`, {
        ...params,
      })
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status == 403) {
          return window.location.replace("/login");
        }
        return;
      });
  } catch (error) {}
};

export const getUserPropertyList = async () => {
  try {
    return axios
      .get(`${process.env.REACT_APP_API_URL}/user-info-property/`)
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status == 403) {
          return window.location.replace("/login");
        }
        return;
      });
  } catch (error) {}
};

export const getUserProperties = async (userId: string) => {
  try {
    return axios
      .get(`${process.env.REACT_APP_API_URL}/user-property/`, {
        params: {
          user_id: userId,
        },
      })
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status == 403) {
          return window.location.replace("/login");
        }
        return;
      });
  } catch (error) {
    return {};
  }
};

export const updateOrCreateUserProperty = async (data: any) => {
  try {
    return axios
      .post(`${process.env.REACT_APP_API_URL}/user-property/`, {
        ...data,
      })
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status == 403) {
          return window.location.replace("/login");
        }
        return false;
      });
  } catch (error) {
    return false;
  }
};

export const fetchClientUserGsts = async (user_id: string) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user-gst/`,
      { params: { user_id } }
    );
    return res.data;
  } catch (error: any) {
    return [];
  }
};

export const verifyClientUserGst = async (gst_number: string) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user-gst/verify/`,
      { gst_number }
    );
    return { ok: true as const, preview: res.data };
  } catch (error: any) {
    const msg = error?.response?.data?.error || "Could not verify GST";
    return { ok: false as const, error: msg };
  }
};

export const backfillClientUserGst = async (
  user_id: string,
  gst_number: string
) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user-gst/backfill/`,
      { user_id, gst_number }
    );
    return { ok: true as const, entry: res.data };
  } catch (error: any) {
    const msg = error?.response?.data?.error || "Could not backfill GST";
    return { ok: false as const, error: msg };
  }
};

export const incrementUserVisit = async (
  userId: string,
  sales_poc?: string,
  branch_id?: number
) => {
  try {
    return axios
      .post(`${process.env.REACT_APP_API_URL}/increment-user-visit/`, {
        user_id: userId,
        sales_poc,
        branch_id,
      })
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((error) => {
        errorActions(error, "Error fetching details. Please try again.");
        if (error.response?.status === 409) {
          return {
            message: "User visit recorded within the past 12 hours already.",
          };
        }
        if (error.response?.status === 403) {
          return window.location.replace("/login");
        }
        return false;
      });
  } catch (error) {
    return false;
  }
};
